import React, { createContext, useContext, useState, useEffect } from 'react'
import { socketService } from '../../views/chat/services/socketService'
import { WhatsAppService } from '../../views/whatsapp/service/whatsapp'
import {
  normalizeConnectionStatus,
  normalizeSocketStatus,
  WhatsAppConnectionState
} from '../../views/whatsapp/utils/connectionStatus'

// Cria o contexto
const WhatsAppStatusContext = createContext()

// Provider que engloba a lógica e disponibiliza o contexto
export function WhatsAppStatusProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false)
  const [statusMessage, setStatusMessage] = useState('')
  const [connectionState, setConnectionState] = useState(WhatsAppConnectionState.IDLE)
  const [isSocketReady, setIsSocketReady] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const authenticatedUser = JSON.parse(localStorage.getItem('userData'))

  const hasPermission =
    authenticatedUser?.ability?.some(
      (perm) => (perm.subject === 'whatsapp' && perm.action === 'read') || (perm.subject === 'all' && perm.action === 'manage')
    )

  const setDefaultStatus = async (organization_id) => {
    try {
      const response = await WhatsAppService.getStatus(organization_id)
      const normalizedStatus = normalizeConnectionStatus(response.data)
      setStatusMessage(normalizedStatus.message)
      setConnectionState(normalizedStatus.state)
      setIsConnected(normalizedStatus.isConnected)
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (!hasPermission) {
      setIsLoading(false)
      return
    }

    const organizationId = authenticatedUser?.organization_id
    const handleSocketConnected = () => {
      socketService.joinWppStatusOrganization(organizationId)
      setIsSocketReady(true)
    }
    const handleConnectionError = () => {
      setStatusMessage('Serviço Temporariamente Indisponível')
      setConnectionState(WhatsAppConnectionState.DEGRADED)
      setIsConnected(false)
      setIsSocketReady(false)
    }
    const handleStatusChange = ({ statusSession }) => {
      const normalizedStatus = normalizeSocketStatus(statusSession)
      if (!normalizedStatus) return
      setStatusMessage(normalizedStatus.message)
      setConnectionState(normalizedStatus.state)
      setIsConnected(normalizedStatus.isConnected)
    }

    const setupSocket = async () => {
      if (!organizationId) return

      try {
        await socketService.connect()
        handleSocketConnected()
        socketService.registerConnectionHandler(handleSocketConnected)
        socketService.registerConnectionErrorHandler(handleConnectionError)
        socketService.wppChangeStatus(handleStatusChange)

        await setDefaultStatus(organizationId)
      } catch (error) {
        console.error('Erro ao conectar socket:', error)
        setStatusMessage('Serviço Temporariamente Indisponível')
        setConnectionState(WhatsAppConnectionState.DEGRADED)
        setIsConnected(false)
      } finally {
        setIsLoading(false)
      }
    }

    setupSocket()

    return () => {
      if (hasPermission) {
        socketService.offConnectionHandler(handleSocketConnected)
        socketService.offConnectionErrorHandler(handleConnectionError)
        socketService.offWppChangeStatus(handleStatusChange)
      }
    }
  }, [hasPermission])

  return (
    <WhatsAppStatusContext.Provider
      value={{ connectionState, isConnected, statusMessage, isSocketReady, isLoading }}
    >
      {children}
    </WhatsAppStatusContext.Provider>
  )
}

// Hook para consumir o contexto facilmente
export function useWhatsAppStatusContext() {
  const context = useContext(WhatsAppStatusContext)
  if (!context) {
    throw new Error(
      'useWhatsAppStatusContext deve ser usado dentro de um WhatsAppStatusProvider'
    )
  }
  return context
}
